import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Menu, X, ChevronDown } from 'lucide-react';
import { useEnquiry } from '../context/EnquiryContext';

const NAV = [
  { label: 'HOME', to: '/' },
  { label: 'ABOUT US', to: '/about' },
  {
    label: 'PROJECTS',
    to: '/projects',
    children: [
      { label: 'Prakruti Enclave', to: '/projects/prakruti-enclave' },
      { label: 'Prakruti Green', to: '/projects/prakruti-green' },
      { label: 'Prakruti Meadows', to: '/projects/prakruti-meadows' },
      { label: 'Prakruti Sunrise', to: '/projects/prakruti-upcoming' },
    ],
  },
  { label: 'GALLERY', to: '/gallery' },
  { label: 'CSR ACTIVITIES', to: '/social-works' },
  { label: 'WHY CHOOSE US', to: '/why-choose-us' },
  { label: 'ABOUT DEVELOPER', to: '/about-developer' },
  { label: 'CONTACT', to: '/contact' },
];

const linkCls = ({ isActive }) =>
  `text-[10px] font-medium tracking-[0.16em] transition-colors duration-300 flex items-center gap-1 whitespace-nowrap ${
    isActive ? 'text-terra' : 'text-ink hover:text-terra'
  }`;

export const Navbar = () => {
  const { open } = useEnquiry();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => setMobileOpen(false), [location]);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 w-full z-50 backdrop-blur-2xl bg-[#F2F0E9]/70 border-b border-[#2B3024]/10 transition-shadow duration-500 ${scrolled ? 'shadow-[0_1px_24px_rgba(28,31,23,0.08)]' : ''}`}
    >
      <div className="max-w-[1440px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-center group shrink-0 leading-none">
          <img src="/assets/prakruti-logo.png" alt="PRAKRUTI Developer — Lead A Better Future" className="h-12 w-auto transition-transform duration-300 group-hover:scale-[1.02]" />
        </Link>

        <nav className="hidden xl:flex items-center gap-5">
          {NAV.map((item) =>
            item.children ? (
              <div key={item.label} className="relative group">
                <NavLink to={item.to} data-testid={`nav-link-${item.label.toLowerCase().replace(/\s/g, '-')}`} className={linkCls}>
                  {item.label} <ChevronDown size={12} className="opacity-60" />
                </NavLink>
                <div className="absolute left-0 top-full pt-4 opacity-0 invisible translate-y-2 group-hover:opacity-100 group-hover:visible group-hover:translate-y-0 transition-all duration-300">
                  <div className="bg-sand border border-clay shadow-[0_16px_40px_rgba(28,31,23,0.12)] min-w-[190px] py-2">
                    {item.children.map((c) => (
                      <Link
                        key={c.label}
                        to={c.to}
                        data-testid={`nav-sub-${c.label.toLowerCase().replace(/\s/g, '-')}`}
                        className="block px-5 py-2.5 text-[11px] tracking-[0.15em] text-ink/70 hover:text-terra hover:bg-stone/60 transition-colors duration-200"
                      >
                        {c.label}
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <NavLink key={item.label} to={item.to} data-testid={`nav-link-${item.label.toLowerCase().replace(/\s/g, '-')}`} className={linkCls} end={item.to === '/'}>
                {item.label}
              </NavLink>
            )
          )}
          <Link
            to="/enquiry"
            data-testid="nav-enquiry-btn"
            className="ml-1 bg-moss text-sand text-[10px] font-medium tracking-[0.18em] px-5 py-3 hover:bg-terra transition-colors duration-300 whitespace-nowrap"
          >
            ENQUIRY
          </Link>
        </nav>

        <button
          className="xl:hidden text-ink"
          onClick={() => setMobileOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {mobileOpen && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
            className="xl:hidden overflow-hidden border-t border-clay bg-sand"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-4">
              {NAV.map((item) => (
                <NavLink key={item.label} to={item.to} data-testid={`nav-mobile-${item.label.toLowerCase().replace(/\s/g, '-')}`} className={linkCls} end={item.to === '/'}>
                  {item.label}
                </NavLink>
              ))}
              <Link
                to="/enquiry"
                data-testid="nav-mobile-enquiry-btn"
                className="mt-2 bg-moss text-sand text-[11px] font-medium tracking-[0.18em] px-6 py-3 hover:bg-terra transition-colors duration-300 w-max"
              >
                ENQUIRY
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
